<template>
  <div
    class="modal fade"
    id="detailsModal"
    tabindex="-1"
    aria-labelledby="detailsModalLabel"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
      <div class="modal-content quotation-details-modal">
        <div class="modal-header border-0 pb-0">
          <div>
            <span class="status-badge" :class="`status-${statusValue}`">
              {{ formatStatus(quotation?.status) }}
            </span>
            <h4 class="modal-title text-title mt-3 mb-2" id="detailsModalLabel">
              {{ $t("quotation_page.quotation_label") }} {{ quotation?.reference_no || "" }}
            </h4>
            <p class="text-muted mb-0">
              {{ quotation?.customer_name || $t("quotation_page.not_available_short") }} •
              {{ quotation?.warehouse_name || $t("quotation_page.not_available_short") }} •
              {{ formatDateTime(quotation?.created_at) }}
            </p>
          </div>
          <button
            type="button"
            class="btn-close p-0"
            data-bs-dismiss="modal"
            :aria-label="$t('common.close')"
          >
            <img
              src="../../../assets/img/icons/close-circle-2.svg"
              :alt="$t('common.close')"
            />
          </button>
        </div>

        <div class="modal-body" v-if="quotation">
          <div class="row g-3 mb-4">
            <div class="col-md-3 col-6">
              <div class="summary-card">
                <span class="summary-label">{{ $t("quotation_page.customer") }}</span>
                <strong class="summary-value">{{ quotation.customer_name || $t("quotation_page.not_available_short") }}</strong>
              </div>
            </div>
            <div class="col-md-3 col-6">
              <div class="summary-card">
                <span class="summary-label">{{ $t("quotation_page.warehouse") }}</span>
                <strong class="summary-value">{{ quotation.warehouse_name || $t("quotation_page.not_available_short") }}</strong>
              </div>
            </div>
            <div class="col-md-3 col-6">
              <div class="summary-card">
                <span class="summary-label">{{ $t("quotation_page.items") }}</span>
                <strong class="summary-value">{{ quotation.items?.length || 0 }}</strong>
              </div>
            </div>
            <div class="col-md-3 col-6">
              <div class="summary-card">
                <span class="summary-label">{{ $t("quotation_page.grand_total") }}</span>
                <strong class="summary-value">${{ Number(quotation.grand_total || 0).toFixed(2) }}</strong>
              </div>
            </div>
          </div>

          <div class="details-section mb-4">
            <div class="section-header">
              <div>
                <h6 class="section-title mb-1">{{ $t("quotation_page.quoted_items") }}</h6>
                <p class="section-subtitle mb-0">{{ $t("quotation_page.review_products") }}</p>
              </div>
            </div>

            <div class="table-responsive quotation-items-table">
              <table class="table align-middle mb-0">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>{{ $t("quotation_page.product") }}</th>
                    <th>{{ $t("quotation_page.code") }}</th>
                    <th class="text-center">{{ $t("quotation_page.qty") }}</th>
                    <th class="text-end">{{ $t("quotation_page.unit_price") }}</th>
                    <th class="text-end">{{ $t("quotation_page.subtotal") }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, index) in quotation.items" :key="`${item.product_id}-${index}`">
                    <td>{{ index + 1 }}</td>
                    <td>{{ item.product_name || $t("quotation_page.not_available_short") }}</td>
                    <td>{{ item.product_code || $t("quotation_page.not_available_short") }}</td>
                    <td class="text-center">{{ item.quantity }}</td>
                    <td class="text-end">${{ Number(item.unit_price || 0).toFixed(2) }}</td>
                    <td class="text-end">${{ itemSubtotal(item).toFixed(2) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div class="details-section">
            <div class="row g-3">
              <div class="col-md-8">
                <h6 class="section-title mb-2">{{ $t("quotation_page.notes") }}</h6>
                <p class="note-block mb-0">{{ quotation.note || $t("quotation_page.no_notes_added") }}</p>

                <div v-if="quotation.attachments && quotation.attachments.length > 0" class="mt-4">
                  <h6 class="section-title mb-3">{{ $t("quotation_page.attachments") }}</h6>
                  <div class="d-flex flex-wrap gap-3">
                    <div v-for="att in quotation.attachments" :key="att.id" class="attachment-item">
                      <a :href="getAttachmentUrl(att)" target="_blank" rel="noreferrer">
                        <img
                          v-if="isImage(att)"
                          :src="getAttachmentUrl(att)"
                          :alt="$t('quotation_page.attachments')"
                          class="rounded-1 border shadow-sm attachment-preview"
                        />
                        <div v-else class="file-icon p-3 bg-light rounded-1 border text-center attachment-preview">
                          <i class="fas fa-file-alt fs-24 mb-2"></i>
                          <p class="fs-12 mb-0 text-truncate">{{ att.file_name || $t("quotation_page.file") }}</p>
                        </div>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-md-4">
                <div class="totals-card">
                  <div class="total-row">
                    <span>{{ $t("quotation_page.subtotal") }}</span>
                    <strong>${{ Number(quotation.total || subtotal).toFixed(2) }}</strong>
                  </div>
                  <div class="total-row">
                    <span>{{ $t("quotation_page.discount") }}</span>
                    <strong>${{ Number(quotation.discount || 0).toFixed(2) }}</strong>
                  </div>
                  <div class="total-row">
                    <span>{{ $t("quotation_page.tax") }}</span>
                    <strong>${{ Number(quotation.tax || 0).toFixed(2) }}</strong>
                  </div>
                  <div class="total-row">
                    <span>{{ $t("quotation_page.shipping") }}</span>
                    <strong>${{ Number(quotation.shipping || 0).toFixed(2) }}</strong>
                  </div>
                  <div class="total-row grand">
                    <span>{{ $t("quotation_page.grand_total") }}</span>
                    <strong>${{ Number(quotation.grand_total || 0).toFixed(2) }}</strong>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-body text-center" v-else>
          <p>{{ $t("quotation_page.loading_quotation_details") }}</p>
        </div>

        <div class="modal-footer border-0 pt-0">
          <button
            type="button"
            class="btn style-five upload-btn px-xxl-6"
            @click="printQuotation"
          >
            {{ $t("quotation_page.print") }}
            <img src="../../../assets/img/icons/download.svg" :alt="$t('quotation_page.print')" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType } from "vue";
import Swal from "sweetalert2";
import { useI18n } from "vue-i18n";

import type { QuotationItem, QuotationRecord } from "@/services/quotation";
import { buildAssetUrl, buildStorageAssetUrl } from "@/utils/settings";

export default defineComponent({
  name: "QuotationDetails",
  props: {
    quotation: {
      type: Object as PropType<QuotationRecord | null>,
      default: null,
    },
  },
  setup(props) {
    const { t, locale } = useI18n();
    const statusValue = computed(() => String(props.quotation?.status || "pending").toLowerCase());
    const subtotal = computed(() =>
      (props.quotation?.items || []).reduce((sum, item) => sum + itemSubtotal(item), 0)
    );

    const formatStatus = (status?: string) => {
      const value = String(status || "pending").toLowerCase();
      return t(`quotation_page.status_${value}`);
    };

    const formatDateTime = (dateString?: string) => {
      if (!dateString) return t("quotation_page.not_available_short");
      return new Date(dateString).toLocaleString(locale.value === "km" ? "km-KH" : "en-GB", {
        timeZone: "Asia/Phnom_Penh",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      });
    };

    const itemSubtotal = (item: QuotationItem) =>
      Number(item.subtotal || Number(item.unit_price || 0) * Number(item.quantity || 0));

    const getAttachmentUrl = (attachment: { file_path?: string; path?: string }) => {
      const filePath = attachment.file_path || attachment.path;
      if (!filePath) return "";

      if (filePath.startsWith("http")) return filePath;
      if (filePath.startsWith("uploads/")) return buildAssetUrl(filePath);
      return buildStorageAssetUrl(filePath);
    };

    const isImage = (attachment: { file_name?: string; path?: string }) => {
      const fileName = attachment.file_name || attachment.path || "";
      return /\.(jpg|jpeg|png|gif|svg|webp)$/i.test(fileName);
    };

    const printQuotation = () => {
      if (!props.quotation) {
        Swal.fire(t("quotation_page.error"), t("quotation_page.no_quotation_selected_to_print"), "error");
        return;
      }

      const rows = (props.quotation.items || [])
        .map(
          (item, index) => `
            <tr>
              <td>${index + 1}</td>
              <td>${item.product_name || t("quotation_page.not_available_short")}</td>
              <td>${item.product_code || t("quotation_page.not_available_short")}</td>
              <td style="text-align:center;">${item.quantity}</td>
              <td style="text-align:right;">$${Number(item.unit_price || 0).toFixed(2)}</td>
              <td style="text-align:right;">$${itemSubtotal(item).toFixed(2)}</td>
            </tr>
          `
        )
        .join("");

      const printMarkup = `
        <html>
          <head>
            <title>${t("quotation_page.quotation_label")} ${props.quotation.reference_no}</title>
            <style>
              @page { size: auto; margin: 14mm; }
              body { font-family: Arial, sans-serif; padding: 32px; color: #1f2940; }
              .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 28px; }
              .status { display: inline-block; padding: 6px 12px; border-radius: 999px; background: #eef4ff; color: #3563e9; font-size: 12px; font-weight: 700; }
              h1 { margin: 12px 0 6px; font-size: 30px; }
              .muted { color: #6b7280; font-size: 14px; }
              .summary { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 24px; }
              .card { border: 1px solid #e6ebf2; border-radius: 12px; padding: 16px; }
              .label { display: block; color: #6b7280; font-size: 12px; margin-bottom: 8px; }
              .value { font-size: 18px; font-weight: 700; }
              table { width: 100%; border-collapse: collapse; margin-top: 8px; }
              th, td { border-bottom: 1px solid #e6ebf2; padding: 12px 10px; font-size: 14px; }
              th { text-align: left; background: #f8fafc; }
              .details { display: grid; grid-template-columns: 1.3fr 0.7fr; gap: 24px; margin-top: 24px; }
              .note { border: 1px dashed #d7deea; border-radius: 14px; padding: 16px; min-height: 120px; }
              .totals { border: 1px solid #e6ebf2; border-radius: 14px; padding: 16px; }
              .total-row { display: flex; justify-content: space-between; padding: 10px 0; border-bottom: 1px solid #e6ebf2; }
              .grand { font-size: 18px; font-weight: 700; }
            </style>
          </head>
          <body>
            <div class="header">
              <div>
                <span class="status">${formatStatus(props.quotation.status)}</span>
                <h1>${t("quotation_page.quotation_label")} ${props.quotation.reference_no}</h1>
                <div class="muted">${props.quotation.customer_name || t("quotation_page.not_available_short")}</div>
                <div class="muted">${props.quotation.warehouse_name || t("quotation_page.not_available_short")}</div>
                <div class="muted">${formatDateTime(props.quotation.created_at)}</div>
              </div>
            </div>

            <div class="summary">
              <div class="card"><span class="label">${t("quotation_page.customer")}</span><div class="value">${props.quotation.customer_name || t("quotation_page.not_available_short")}</div></div>
              <div class="card"><span class="label">${t("quotation_page.warehouse")}</span><div class="value">${props.quotation.warehouse_name || t("quotation_page.not_available_short")}</div></div>
              <div class="card"><span class="label">${t("quotation_page.items")}</span><div class="value">${props.quotation.items?.length || 0}</div></div>
              <div class="card"><span class="label">${t("quotation_page.grand_total")}</span><div class="value">$${Number(props.quotation.grand_total || 0).toFixed(2)}</div></div>
            </div>

            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>${t("quotation_page.product")}</th>
                  <th>${t("quotation_page.code")}</th>
                  <th style="text-align:center;">${t("quotation_page.qty")}</th>
                  <th style="text-align:right;">${t("quotation_page.unit_price")}</th>
                  <th style="text-align:right;">${t("quotation_page.subtotal")}</th>
                </tr>
              </thead>
              <tbody>${rows}</tbody>
            </table>

            <div class="details">
              <div>
                <h3>${t("quotation_page.notes")}</h3>
                <div class="note">${props.quotation.note || t("quotation_page.no_notes_added")}</div>
              </div>
              <div class="totals">
                <div class="total-row"><span>${t("quotation_page.subtotal")}</span><strong>$${Number(props.quotation.total || subtotal.value).toFixed(2)}</strong></div>
                <div class="total-row"><span>${t("quotation_page.discount")}</span><strong>$${Number(props.quotation.discount || 0).toFixed(2)}</strong></div>
                <div class="total-row"><span>${t("quotation_page.tax")}</span><strong>$${Number(props.quotation.tax || 0).toFixed(2)}</strong></div>
                <div class="total-row"><span>${t("quotation_page.shipping")}</span><strong>$${Number(props.quotation.shipping || 0).toFixed(2)}</strong></div>
                <div class="total-row grand"><span>${t("quotation_page.grand_total")}</span><strong>$${Number(props.quotation.grand_total || 0).toFixed(2)}</strong></div>
              </div>
            </div>
          </body>
        </html>
      `;

      const iframe = document.createElement("iframe");
      iframe.style.position = "fixed";
      iframe.style.right = "0";
      iframe.style.bottom = "0";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "0";
      iframe.setAttribute("aria-hidden", "true");
      document.body.appendChild(iframe);

      const iframeWindow = iframe.contentWindow;
      const iframeDocument = iframe.contentDocument || iframeWindow?.document;

      if (!iframeWindow || !iframeDocument) {
        document.body.removeChild(iframe);
        Swal.fire(t("quotation_page.print_failed"), t("quotation_page.unable_to_prepare_quotation_print"), "error");
        return;
      }

      iframeDocument.open();
      iframeDocument.write(printMarkup);
      iframeDocument.close();

      let hasPrinted = false;
      const cleanup = () => {
        setTimeout(() => {
          if (document.body.contains(iframe)) {
            document.body.removeChild(iframe);
          }
        }, 300);
      };

      const triggerPrint = () => {
        if (hasPrinted) return;
        hasPrinted = true;
        iframeWindow.focus();
        iframeWindow.print();
        iframeWindow.onafterprint = cleanup;
      };

      iframe.onload = () => {
        setTimeout(triggerPrint, 250);
      };

      setTimeout(() => {
        try {
          triggerPrint();
        } catch {
          cleanup();
        }
      }, 600);
    };

    return {
      statusValue,
      subtotal,
      formatStatus,
      formatDateTime,
      itemSubtotal,
      getAttachmentUrl,
      isImage,
      printQuotation,
      t,
    };
  },
});
</script>

<style scoped>
.quotation-details-modal {
  border: 0;
  border-radius: 20px;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 92px;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
}

.status-completed {
  color: #1f9254;
  background: #e8f7ee;
}

.status-pending {
  color: #d33c43;
  background: #fdecec;
}

.status-sent {
  color: #3563e9;
  background: #edf4ff;
}

.summary-card,
.details-section,
.totals-card {
  background: #fff;
  border: 1px solid #e8edf5;
  border-radius: 16px;
}

.summary-card {
  padding: 18px;
}

.summary-label {
  display: block;
  margin-bottom: 10px;
  color: #7f8aa3;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.summary-value {
  color: #1f2940;
  font-size: 18px;
}

.details-section {
  padding: 20px;
}

.section-title {
  color: #1f2940;
  font-size: 20px;
  font-weight: 700;
}

.section-subtitle {
  color: #7f8aa3;
  font-size: 14px;
}

.quotation-items-table thead th {
  border-bottom: 1px solid #e8edf5;
  color: #1f2940;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.quotation-items-table tbody td {
  color: #4b5565;
  padding-top: 14px;
  padding-bottom: 14px;
}

.note-block {
  min-height: 120px;
  border: 1px dashed #d7deea;
  border-radius: 14px;
  padding: 16px;
  color: #4b5565;
}

.totals-card {
  padding: 18px;
}

.total-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #e8edf5;
  color: #4b5565;
}

.total-row:last-child {
  border-bottom: 0;
  padding-bottom: 0;
}

.total-row.grand {
  color: #1f2940;
  font-size: 18px;
}

.attachment-preview {
  width: 150px;
  height: 150px;
  object-fit: cover;
}
</style>
